import { useCallback, useMemo } from 'react';
import { type UseFieldArrayUpdate, useFormContext, useWatch } from 'react-hook-form';
import { MatchingOperator } from '../../../../../../modules/audiences/AudienceForm/constants.ts';
import { useNormalizedDefinitionValidation } from '../../../../../../modules/audiences/AudienceForm/hooks/useNormalizedDefinitionValidation.ts';
import type {
  AudienceForm,
  FormSegment,
  GroupFieldName,
  GroupsPath,
} from '../../../../../../modules/audiences/AudienceForm/types.ts';
import { isGroup } from '../components/utils/guards.ts';
import type { SelectedLocationSegment } from '../constants.ts';
import { getGroupFromPath } from '../utils/getGroupFromPath.ts';

type SegmentSetter = (segments: FormSegment[] | ((prev: FormSegment[]) => FormSegment[])) => void;

export type UseLocationSectionProps = {
  groupFieldName: GroupFieldName;
  update: UseFieldArrayUpdate<AudienceForm, GroupsPath>;
  index: number;
};

export type UseLocationSectionResponse = {
  locationStateSegments: FormSegment[];
  locationZipCodeSegments: FormSegment[];
  locationDmaSegments: FormSegment[];
  locationCongressionalSegments: FormSegment[];
  setLocationStateSegments: SegmentSetter;
  setLocationZipCodeSegments: SegmentSetter;
  setLocationDmaSegments: SegmentSetter;
  setLocationCongressionalSegments: SegmentSetter;
  numberOfLocationItemsSelected: number;
  clearAllLocation: (event?: React.MouseEvent<HTMLButtonElement>) => void;
};

export const useLocationSection = ({
  groupFieldName,
  update,
  index,
}: UseLocationSectionProps): UseLocationSectionResponse => {
  const { control } = useFormContext<AudienceForm>();
  const { withValidation } = useNormalizedDefinitionValidation();

  const updateWithValidation = useCallback(withValidation(update), []);

  const observableNormalizedDefinition = useWatch({
    name: 'normalized_definition',
    control,
  });

  const watchedGroup = useMemo(
    () => getGroupFromPath(observableNormalizedDefinition, groupFieldName),
    [observableNormalizedDefinition, groupFieldName]
  );

  const allLocationSegments = useMemo(() => {
    if (!isGroup(watchedGroup)) return [];

    return watchedGroup.location?.segments ?? [];
  }, [watchedGroup]);

  const updateLocationSegments = useCallback(
    (updatedSegments: FormSegment[]) => {
      const currentGroup = watchedGroup;

      if (!isGroup(currentGroup)) return;

      updateWithValidation(index, {
        ...currentGroup,
        location: {
          ...(currentGroup.location ?? {}),
          segments: updatedSegments,
          operator: MatchingOperator.UNION,
        },
      });
    },
    [updateWithValidation, index, watchedGroup]
  );

  const { locationStateSegments, locationZipCodeSegments, locationDmaSegments, locationCongressionalSegments } =
    useMemo(() => {
      const result = {
        locationStateSegments: [] as FormSegment[],
        locationZipCodeSegments: [] as FormSegment[],
        locationDmaSegments: [] as FormSegment[],
        locationCongressionalSegments: [] as FormSegment[],
      };

      allLocationSegments.forEach(segment => {
        switch (segment.sub_category) {
          case 'state':
            result.locationStateSegments.push(segment);
            break;
          case 'zip':
            result.locationZipCodeSegments.push(segment);
            break;
          case 'dma':
            result.locationDmaSegments.push(segment);
            break;
          case 'congressional':
            result.locationCongressionalSegments.push(segment);
            break;
        }
      });

      return result;
    }, [allLocationSegments]);

  const createSegmentSetter = useCallback(
    (subCategory: SelectedLocationSegment, currentSegments: FormSegment[]): SegmentSetter => {
      return (segments: FormSegment[] | ((prev: FormSegment[]) => FormSegment[])) => {
        const newSegments = typeof segments === 'function' ? segments(currentSegments) : segments;

        const updatedSegments = [
          ...allLocationSegments.filter(segment => segment.sub_category !== subCategory),
          ...newSegments,
        ];

        updateLocationSegments(updatedSegments);
      };
    },
    [allLocationSegments, updateLocationSegments]
  );

  const setLocationStateSegments = useMemo(
    () => createSegmentSetter('state', locationStateSegments),
    [createSegmentSetter, locationStateSegments]
  );

  const setLocationZipCodeSegments = useMemo(
    () => createSegmentSetter('zip', locationZipCodeSegments),
    [createSegmentSetter, locationZipCodeSegments]
  );

  const setLocationDmaSegments = useMemo(
    () => createSegmentSetter('dma', locationDmaSegments),
    [createSegmentSetter, locationDmaSegments]
  );

  const setLocationCongressionalSegments = useMemo(
    () => createSegmentSetter('congressional', locationCongressionalSegments),
    [createSegmentSetter, locationCongressionalSegments]
  );

  const numberOfLocationItemsSelected = useMemo(
    () =>
      locationStateSegments.length +
      locationZipCodeSegments.length +
      locationDmaSegments.length +
      locationCongressionalSegments.length,
    [locationStateSegments, locationZipCodeSegments, locationDmaSegments, locationCongressionalSegments]
  );

  const clearAllLocation = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();

      updateLocationSegments([]);
    },
    [updateLocationSegments]
  );

  return {
    locationStateSegments,
    locationZipCodeSegments,
    locationDmaSegments,
    locationCongressionalSegments,
    setLocationStateSegments,
    setLocationZipCodeSegments,
    setLocationDmaSegments,
    setLocationCongressionalSegments,
    numberOfLocationItemsSelected,
    clearAllLocation,
  };
};
